// Port web de expo-location, restreint aux API réellement utilisées par l'app
// (relevé sur la base native : getCurrentPositionAsync, watchPositionAsync,
// getLastKnownPositionAsync, request/getForegroundPermissionsAsync, Accuracy).
//
// L'API publique reproduit celle d'expo-location pour que le portage des
// écrans se limite à changer le chemin d'import.
//
// ⚠️ Deux écarts de fond avec le natif :
//
//  1. PERMISSION — `navigator.geolocation` ne permet pas de DEMANDER la
//     permission sans demander en même temps une position. `requestForeground-
//     PermissionsAsync()` déclenche donc une vraie localisation, dont on
//     réutilise le résultat comme « dernière position connue ». C'est plus
//     coûteux qu'en natif, et il ne faut pas l'appeler en boucle.
//
//  2. HTTPS OBLIGATOIRE — la géolocalisation est refusée sur origine non
//     sécurisée. En développement, `localhost` est considéré sûr ; un test
//     depuis un téléphone sur l'IP LAN (http://192.168.x.x) échouera, il faut
//     un tunnel HTTPS.

export enum Accuracy {
  Lowest = 1,
  Low = 2,
  Balanced = 3,
  High = 4,
  Highest = 5,
  BestForNavigation = 6,
}

export type PermissionStatus = "granted" | "denied" | "undetermined";

export type PermissionResponse = {
  status: PermissionStatus;
  granted: boolean;
  canAskAgain: boolean;
};

export type LocationObject = {
  coords: {
    latitude: number;
    longitude: number;
    altitude: number | null;
    accuracy: number | null;
    altitudeAccuracy: number | null;
    heading: number | null;
    speed: number | null;
  };
  timestamp: number;
};

export type LocationOptions = {
  accuracy?: Accuracy;
  /** Ignoré sur web : le navigateur ne l'expose pas. Conservé pour compat. */
  distanceInterval?: number;
  timeInterval?: number;
};

export type LocationSubscription = { remove: () => void };

// `getLastKnownPositionAsync` n'a pas d'équivalent navigateur : on mémorise la
// dernière position obtenue pendant la vie de l'onglet. Au premier chargement,
// il n'y a donc rien — contrairement au natif, où l'OS gardait un cache.
let lastKnown: LocationObject | null = null;

function toLocationObject(pos: GeolocationPosition): LocationObject {
  const out: LocationObject = {
    coords: {
      latitude: pos.coords.latitude,
      longitude: pos.coords.longitude,
      altitude: pos.coords.altitude,
      accuracy: pos.coords.accuracy,
      altitudeAccuracy: pos.coords.altitudeAccuracy,
      heading: pos.coords.heading,
      speed: pos.coords.speed,
    },
    timestamp: pos.timestamp,
  };
  lastKnown = out;
  return out;
}

// Au-delà de Balanced, on demande la haute précision (GPS sur mobile), plus
// lente et plus gourmande — d'où le seuil plutôt qu'un mapping systématique.
function toPositionOptions(options?: LocationOptions): PositionOptions {
  const accuracy = options?.accuracy ?? Accuracy.Balanced;
  return {
    enableHighAccuracy: accuracy >= Accuracy.High,
    timeout: 15_000,
    maximumAge: options?.timeInterval ?? 10_000,
  };
}

function isDenied(err: GeolocationPositionError): boolean {
  return err.code === err.PERMISSION_DENIED;
}

/**
 * Lit l'état de la permission SANS la demander, via l'API Permissions.
 * Firefox ne l'implémente pas pour la géolocalisation : on renvoie alors
 * `undetermined`, ce qui pousse l'appelant à passer par la demande explicite.
 */
export async function getForegroundPermissionsAsync(): Promise<PermissionResponse> {
  if (!navigator.permissions?.query) {
    return { status: "undetermined", granted: false, canAskAgain: true };
  }
  try {
    const res = await navigator.permissions.query({ name: "geolocation" });
    const status: PermissionStatus =
      res.state === "granted" ? "granted" : res.state === "denied" ? "denied" : "undetermined";
    return { status, granted: status === "granted", canAskAgain: status !== "denied" };
  } catch {
    return { status: "undetermined", granted: false, canAskAgain: true };
  }
}

/**
 * Demande la permission. Déclenche nécessairement une localisation réelle
 * (cf. écart n°1 en tête de fichier) — son résultat alimente le cache
 * `lastKnown`, donc l'appel n'est pas perdu.
 */
export async function requestForegroundPermissionsAsync(): Promise<PermissionResponse> {
  if (!navigator.geolocation) {
    return { status: "denied", granted: false, canAskAgain: false };
  }
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        toLocationObject(pos);
        resolve({ status: "granted", granted: true, canAskAgain: false });
      },
      (err) => {
        // Un timeout ou une position indisponible ne veut PAS dire refus : la
        // permission peut être accordée sans qu'aucun capteur ne réponde.
        const denied = isDenied(err);
        resolve({
          status: denied ? "denied" : "undetermined",
          granted: false,
          canAskAgain: !denied,
        });
      },
      toPositionOptions(),
    );
  });
}

export async function getCurrentPositionAsync(
  options?: LocationOptions,
): Promise<LocationObject> {
  if (!navigator.geolocation) {
    throw new Error("[location] Géolocalisation non supportée par ce navigateur");
  }
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve(toLocationObject(pos)),
      (err) => reject(new Error(`[location] ${err.message} (code ${err.code})`)),
      toPositionOptions(options),
    );
  });
}

/**
 * Dernière position obtenue DEPUIS L'OUVERTURE DE L'ONGLET, ou `null`.
 * Le natif interrogeait le cache de l'OS et répondait souvent immédiatement au
 * premier lancement ; ici le premier appel d'une session rend toujours `null`.
 * Les appelants doivent traiter ce cas comme un chemin normal, pas une erreur.
 */
export async function getLastKnownPositionAsync(): Promise<LocationObject | null> {
  return lastKnown;
}

export async function watchPositionAsync(
  options: LocationOptions,
  callback: (location: LocationObject) => void,
): Promise<LocationSubscription> {
  if (!navigator.geolocation) {
    return { remove: () => {} };
  }
  const id = navigator.geolocation.watchPosition(
    (pos) => callback(toLocationObject(pos)),
    () => {
      // Un échec ponctuel (tunnel, GPS perdu) ne doit pas tuer l'abonnement :
      // le navigateur continue d'émettre dès que la position revient.
    },
    toPositionOptions(options),
  );
  return { remove: () => navigator.geolocation.clearWatch(id) };
}
